import { Controller } from "@hotwired/stimulus"
import { getUserPref, setUserPref } from "user_prefs"
import { renderIcons } from "lucide_icons"
import { findOpenQuestions, TBD_MARKER_RX } from "tbd_questions"

// Editor-native controller — owns the sidebar/active-section state for
// the Rails-native editor shell. Plan data still lives in localStorage
// (per SOW DFLW-001B); this controller only manages WHICH section is
// visible and lazy-initialises section-level controllers as needed.
const PANE_PREF_KEY  = "pcp_native_pane_vis"
const SPLIT_PREF_KEY = "pcp_native_split_px"
const SIDE_PREF_KEY  = "pcp_native_side_collapsed"
const MIN_PANE_PX    = 320
const SIDEBAR_PX     = 280
const DIVIDER_PX     = 6

// Sidebar section badges read the plan straight from localStorage
// (same key the section controllers persist to). TBD_SKIP mirrors
// pcp_builder.html#countTBD — id/photo/signature blobs never carry a
// meaningful "TBD".
const PLAN_KEY  = "pcp_plan_data"
const TBD_SKIP  = new Set(["id", "photo", "signature", "lifePhoto"])

export default class extends Controller {
  static targets = ["navLink", "editorPane", "wizardPane", "toggleEditorBtn", "toggleWizardBtn", "divider", "sideToggleBtn", "tbdFlag", "tbdFlagCount"]
  static values  = { activeSection: String }

  connect() {
    this.showSection(this.activeSectionValue || "client")
    this._restorePanePrefs()
    this._restoreSplit()
    this._restoreSidebarState()
    this._wireSubblockFocus()
    // Wizard pane drives section changes too — when the user clicks
    // wizard Next/Prev/Jump, the wizard dispatches
    // `wizard:section-change` and the editor follows so both panes
    // stay pinned to the same section. The `id === activeSectionValue`
    // guard breaks the editor→wizard→editor loop the wizard's own
    // `idx === this.step` guard would otherwise hit.
    this._onWizardSectionChange = (e) => {
      const id = e.detail?.sectionId
      if (!id || id === this.activeSectionValue) return
      this.activeSectionValue = id
      this.showSection(id)
    }
    document.addEventListener("wizard:section-change", this._onWizardSectionChange)
    // Sidebar section badges — the "N TBD" count + "real name → use
    // [FirstName]" alert. Computed ONCE on load, NOT on every edit: the
    // live per-keystroke whole-plan rescan was the editor's main
    // slowdown, so open-question tracking now lives in the on-demand
    // Open Questions section (scanned when opened / on Rescan). The
    // badges refresh on reload or when the editor reconnects.
    this._refreshSectionBadges()
    // Toolbar review counter — unlike the per-section sidebar badges (load-only
    // for perf), this single running total is kept live so a flag/TBD edit
    // updates it. One cheap scan, coalesced 300ms off the per-keystroke path.
    this._refreshTbdFlag()
    this._onPlanChangeFlag = () => {
      clearTimeout(this._flagTimer)
      this._flagTimer = setTimeout(() => this._refreshTbdFlag(), 300)
    }
    document.addEventListener("plan:change", this._onPlanChangeFlag)
    // Keep a field's amber TBD highlight in sync as the user types (cheap —
    // only re-checks the single edited field, not the whole plan).
    this._onFieldInput = (e) => {
      const el = e.target
      if (el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT")) this._markTbdField(el)
    }
    this.element.addEventListener("input", this._onFieldInput)
    // Render any lucide icons that the partials placed.
    if (window.lucide && typeof window.lucide.createIcons === "function") {
      renderIcons(this.element)
    }
  }

  disconnect() {
    if (this._onSubFocusIn) document.removeEventListener("focusin", this._onSubFocusIn)
    if (this._onSubClick)   document.removeEventListener("click",  this._onSubClick)
    if (this._onWizardSectionChange) document.removeEventListener("wizard:section-change", this._onWizardSectionChange)
    if (this._badgeTimer) clearTimeout(this._badgeTimer)
    if (this._onPlanChangeFlag) document.removeEventListener("plan:change", this._onPlanChangeFlag)
    if (this._onFieldInput) this.element.removeEventListener("input", this._onFieldInput)
    if (this._flagTimer) clearTimeout(this._flagTimer)
  }

  // Tell the wizard which sub-block in the editor pane has the user's
  // attention (e.g. "About → Who Lives With Me?", or "Circle of Support
  // → Family row") so it can scope its suggestions. Delegated listener
  // covers every list block + CoS row without needing per-partial wiring.
  _wireSubblockFocus() {
    const inEditorPane = (el) => {
      // Restrict to the LEFT (editor) pane <main class="en-main"> —
      // focusin inside the wizard pane on the right is the wizard's
      // own UI and must not loop back.
      if (!el || !el.closest) return false
      if (el.closest(".en-wiz-pane")) return false
      return !!el.closest(".en-main")
    }
    const findBlock = (el) => {
      if (!el || el === document) return null
      const dl  = el.closest?.("[data-dynamic-list-path-value]")
      const row = el.closest?.("[data-row]")
      // Row-with-relationship (Circle of Support): combine the
      // dl path with the row's category so the wizard can auto-open
      // the matching accordion.
      const relSel = row?.querySelector?.('[data-field="relationship"]')
      if (row && relSel) {
        return {
          section: this.activeSectionValue,
          path: dl?.dataset?.dynamicListPathValue || "about.circleOfSupport",
          category: relSel.value || null,
          rowId: row?.dataset?.id || null
        }
      }
      if (dl) {
        return {
          section: this.activeSectionValue,
          path: dl.dataset.dynamicListPathValue,
          // Circle-of-Support group: its default relationship (Family/Friend/
          // Professional/Community) is the category, so the wizard isolates the
          // right group even when it's empty / clicked on the header.
          category: dl.dataset.dynamicListDefaultRelationshipValue || null,
          // The clicked row's id (when the click lands on an existing entry) so
          // the wizard can highlight/scroll to THAT person for editing.
          rowId: row?.dataset?.id || null
        }
      }
      // Scalar fields (plan_input / plan_select — Primary language, Cultural
      // preferences, Living situation) carry data-plan-field on the input.
      // Resolve whether the user clicks the input itself or its label/wrapper
      // (.mb-4[data-input-wrap]) so clicking one isolates it in the wizard.
      const pfEl = el.closest?.("[data-plan-field]") ||
                   el.closest?.("[data-input-wrap]")?.querySelector?.("[data-plan-field]")
      if (pfEl?.dataset?.planField) {
        return {
          section: this.activeSectionValue,
          path: pfEl.dataset.planField,
          category: null,
          rowId: null
        }
      }
      return null
    }
    const emit = (block) => {
      if (!block) return
      const same = this._lastSubFocus &&
                   this._lastSubFocus.path === block.path &&
                   this._lastSubFocus.category === block.category &&
                   this._lastSubFocus.rowId === block.rowId
      if (same) return
      this._lastSubFocus = block
      document.dispatchEvent(new CustomEvent("editor-native:subblock-focus", {
        detail: block
      }))
    }
    this._onSubFocusIn = (e) => {
      if (!inEditorPane(e.target)) return
      emit(findBlock(e.target))
    }
    this._onSubClick = (e) => {
      if (!inEditorPane(e.target)) return
      emit(findBlock(e.target))
    }
    document.addEventListener("focusin", this._onSubFocusIn)
    document.addEventListener("click",   this._onSubClick)
  }

  // ---- Sidebar collapse / expand ----
  // Flip the .is-side-collapsed class on .en-app; the CSS shrinks the
  // sidebar column from 280px to 64px and hides text labels, leaving
  // the colored icons. The browser-native `title` attribute on each
  // .en-sidebar-link surfaces the section name on hover in collapsed
  // mode. Persisted to localStorage so the layout survives a refresh.
  toggleSidebar() {
    const collapsed = this.element.classList.toggle("is-side-collapsed")
    if (this.hasSideToggleBtnTarget) {
      this.sideToggleBtnTarget.title = collapsed ? "Expand sidebar" : "Collapse sidebar"
      this.sideToggleBtnTarget.setAttribute("aria-label", collapsed ? "Expand sidebar" : "Collapse sidebar")
    }
    try { localStorage.setItem(SIDE_PREF_KEY, collapsed ? "1" : "0") } catch (_) {}
    // Mirror to the server so the same user gets the same state
    // when they sign in on another device.
    setUserPref("sidebar_collapsed", collapsed)
  }

  _restoreSidebarState() {
    try {
      // localStorage wins (instant); fall back to the server-stored
      // user pref when this device hasn't seen the editor before.
      let stored = localStorage.getItem(SIDE_PREF_KEY)
      if (stored == null) stored = getUserPref("sidebar_collapsed") ? "1" : "0"
      if (stored === "1") {
        this.element.classList.add("is-side-collapsed")
        if (this.hasSideToggleBtnTarget) {
          this.sideToggleBtnTarget.title = "Expand sidebar"
          this.sideToggleBtnTarget.setAttribute("aria-label", "Expand sidebar")
        }
      }
    } catch (_) {}
  }


  // ---- Editor/Wizard split (draggable divider) ----
  // The divider lives between .en-main and .en-wiz-pane and rewrites
  // --en-main-w on .en-app as the user drags. The wizard column is
  // `1fr`, so it absorbs the slack — bigger editor = smaller wizard
  // and vice versa. Persisted to localStorage so the layout survives
  // a refresh.
  startResize(event) {
    event.preventDefault()
    this._resizing = true
    this.element.classList.add("is-resizing")
    this._onMouseMove = (e) => this._handleResize(e)
    this._onMouseUp   = ()  => this._endResize()
    document.addEventListener("mousemove", this._onMouseMove)
    document.addEventListener("mouseup",   this._onMouseUp)
  }

  _handleResize(event) {
    if (!this._resizing) return
    // Use the editor pane's live left edge as the anchor — the
    // sidebar can be collapsed (280px → 64px), so the hardcoded
    // SIDEBAR_PX would offset the cursor from the divider by ~216px
    // whenever the sidebar is shrunk. Measuring the actual pane
    // makes the divider follow the cursor regardless of sidebar
    // state.
    if (!this.hasEditorPaneTarget) return
    const appRect  = this.element.getBoundingClientRect()
    const paneLeft = this.editorPaneTarget.getBoundingClientRect().left
    const rawMain  = event.clientX - paneLeft
    const available = appRect.right - paneLeft - DIVIDER_PX
    // Clamp so neither pane collapses below MIN_PANE_PX. The wizard's
    // floor is enforced by capping the editor's ceiling.
    const mainPx = Math.max(MIN_PANE_PX, Math.min(rawMain, available - MIN_PANE_PX))
    this.element.style.setProperty("--en-main-w", `${mainPx}px`)
  }

  _endResize() {
    this._resizing = false
    this.element.classList.remove("is-resizing")
    document.removeEventListener("mousemove", this._onMouseMove)
    document.removeEventListener("mouseup",   this._onMouseUp)
    // Persist the final width so a refresh comes back to the same split.
    const px = this.element.style.getPropertyValue("--en-main-w")
    if (px) {
      try { localStorage.setItem(SPLIT_PREF_KEY, px) } catch (_) {}
    }
  }

  // Double-click the divider to clear the saved split and fall back to
  // the CSS default (1.4fr : 1fr ≈ 58/42).
  resetSplit() {
    this.element.style.removeProperty("--en-main-w")
    try { localStorage.removeItem(SPLIT_PREF_KEY) } catch (_) {}
  }

  _restoreSplit() {
    try {
      const px = localStorage.getItem(SPLIT_PREF_KEY)
      if (px) this.element.style.setProperty("--en-main-w", px)
    } catch (_) {}
  }

  // ---- Pane visibility toggles ----
  toggleEditorPane() { this._togglePane("editor") }
  toggleWizardPane() { this._togglePane("wizard") }

  // Toolbar flag counter — jump to the Open Questions review. That review UI
  // lives in the editor (left) pane, so ensure it's visible first (the user
  // may be in wizard-only mode) before activating the section.
  reviewFlags() {
    if (this.element.classList.contains("is-editor-hidden")) {
      this.element.classList.remove("is-editor-hidden")
      this._syncToggleButtons()
      this._savePanePrefs()
    }
    this.activeSectionValue = "openQuestions"
    this.showSection("openQuestions")
  }

  _togglePane(which) {
    const cls = which === "editor" ? "is-editor-hidden" : "is-wiz-hidden"
    const otherCls = which === "editor" ? "is-wiz-hidden" : "is-editor-hidden"
    // Don't allow BOTH to be hidden — if the user tries, show the other
    // pane back instead so the shell never goes blank.
    const wasHidden = this.element.classList.contains(cls)
    if (wasHidden) {
      this.element.classList.remove(cls)
    } else {
      this.element.classList.add(cls)
      this.element.classList.remove(otherCls)
    }
    this._syncToggleButtons()
    this._savePanePrefs()
    renderIcons(this.element)
  }

  _syncToggleButtons() {
    const editorHidden = this.element.classList.contains("is-editor-hidden")
    const wizHidden    = this.element.classList.contains("is-wiz-hidden")
    if (this.hasToggleEditorBtnTarget) {
      this.toggleEditorBtnTarget.classList.toggle("is-pressed", !editorHidden)
    }
    if (this.hasToggleWizardBtnTarget) {
      this.toggleWizardBtnTarget.classList.toggle("is-pressed", !wizHidden)
    }
  }

  _savePanePrefs() {
    try {
      localStorage.setItem(PANE_PREF_KEY, JSON.stringify({
        editorHidden: this.element.classList.contains("is-editor-hidden"),
        wizHidden:    this.element.classList.contains("is-wiz-hidden")
      }))
    } catch (_) {}
  }

  _restorePanePrefs() {
    try {
      const raw = localStorage.getItem(PANE_PREF_KEY)
      if (!raw) { this._syncToggleButtons(); return }
      const p = JSON.parse(raw)
      if (p.editorHidden) this.element.classList.add("is-editor-hidden")
      if (p.wizHidden)    this.element.classList.add("is-wiz-hidden")
    } catch (_) {}
    this._syncToggleButtons()
  }

  setActive(event) {
    const btn = event.currentTarget
    const key = btn.dataset.sectionKey
    if (!key) return
    this.activeSectionValue = key
    this.showSection(key)
    // Push state so refresh / share links land on the same section.
    try {
      const next = `/editor/native/${key}`
      if (window.location.pathname !== next) {
        window.history.replaceState({}, "", next)
      }
    } catch (_) { /* noop */ }
  }

  showSection(key) {
    this.element.querySelectorAll("[data-section]").forEach(el => {
      const visible = el.dataset.section === key
      if (visible) {
        // Lazy-mount: on first view, move the section's parked <template>
        // content into the live DOM so its Stimulus controllers connect.
        // Done BEFORE un-hiding so plan_store's hidden-attribute observer
        // hydrates the freshly-mounted inputs.
        this._instantiateSection(el)
        el.removeAttribute("hidden")
        // Amber-flag every field in the now-visible section that still holds a
        // TBD / ?? marker, so an unfinished value pops right where it lives.
        queueMicrotask(() => this._highlightTbdFields(el))
      } else {
        el.setAttribute("hidden", "")
      }
    })
    this.navLinkTargets.forEach(link => {
      link.classList.toggle("is-active", link.dataset.sectionKey === key)
    })
    // Tell the embedded wizard to follow along so the right pane shows
    // the same section as the left. Deferred one tick so the wizard
    // controller has had a chance to connect on first paint.
    queueMicrotask(() => {
      document.dispatchEvent(new CustomEvent("editor-native:section-change", {
        detail: { sectionId: key }
      }))
    })
    // Refresh icons inside any newly visible section.
    if (window.lucide && typeof window.lucide.createIcons === "function") {
      renderIcons(this.element)
    }
  }

  // ---- TBD field highlight (amber flag right on the unfinished field) ----

  // Toggle the amber TBD outline on a single input/textarea based on its value.
  _markTbdField(el) {
    if (!el || (el.type && ["hidden", "password", "checkbox", "radio", "file", "button", "submit"].includes(el.type))) return
    el.classList.toggle("pcp-tbd-field", TBD_MARKER_RX.test(el.value || ""))
  }

  // Flag every TBD-bearing field within a container (a freshly shown section).
  _highlightTbdFields(root) {
    if (!root) return
    root.querySelectorAll("input, textarea").forEach(el => this._markTbdField(el))
  }

  // Move a lazy section's parked <template> content into the live DOM (once),
  // which connects its Stimulus controllers. No-op for the active section
  // (rendered live) or a section that's already been instantiated.
  _instantiateSection(sectionEl) {
    const tpl = sectionEl.querySelector(":scope > template[data-lazy-content]")
    if (!tpl) return
    sectionEl.appendChild(tpl.content.cloneNode(true))
    tpl.remove()
    renderIcons(sectionEl)
  }

  // ---- Sidebar section badges (TBD count + real-name-usage alert) ----
  // Ports of pcp_builder.html#getSectionTBDCount / getSectionNameCount so
  // the native rail flags the same things as the legacy editor: how many
  // "TBD" items remain per section, and whether the client's real name was
  // typed where a [FirstName] token should be.

  _scheduleBadgeRefresh() {
    // plan:change fires on every keystroke; coalesce so the full-plan
    // scan runs at most ~5×/sec instead of per character.
    if (this._badgeTimer) clearTimeout(this._badgeTimer)
    this._badgeTimer = setTimeout(() => this._refreshSectionBadges(), 200)
  }

  _loadPlan() {
    try {
      const raw = localStorage.getItem(PLAN_KEY)
      return raw ? JSON.parse(raw) : null
    } catch (_) { return null }
  }

  // Recursively count whole-word "TBD" across strings/arrays/objects,
  // skipping id/photo/signature/lifePhoto blobs.
  _countTBD(data) {
    if (typeof data === "string") return (data.match(/\bTBD\b/gi) || []).length
    if (Array.isArray(data)) return data.reduce((n, v) => n + this._countTBD(v), 0)
    if (data && typeof data === "object") {
      let n = 0
      for (const k of Object.keys(data)) { if (!TBD_SKIP.has(k)) n += this._countTBD(data[k]) }
      return n
    }
    return 0
  }

  _sectionTBDCount(plan, key) {
    if (!plan) return 0
    const about = plan.about || {}
    let data
    switch (key) {
      case "client":           data = plan.client; break
      case "facilitator":      data = plan.facilitator; break
      case "about":            data = { whoLivesWithMe: about.whoLivesWithMe, communicationStyle: about.communicationStyle, schoolDetails: about.schoolDetails, workDetails: about.workDetails }; break
      case "circleOfSupport":  data = about.circleOfSupport; break
      case "communication":    data = about.communicationChart; break
      case "wonderfulThings":  data = plan.wonderfulThings; break
      case "notSoFavorites":   data = plan.notSoFavorites; break
      case "dayInLife":        data = plan.dayInLife; break
      case "resourceCalendar": return this._countTBD((plan.resourceCalendar || {}).notes || "")
      case "important":        data = plan.important; break
      case "bigDreams":        data = plan.bigDreams; break
      case "goals":            data = plan.goals; break
      case "working":          data = plan.working; break
      case "notWorking":       data = plan.working; break
      case "supportMatch":     data = plan.supportMatch; break
      case "currentServices":  data = plan.currentServices; break
      case "barriers":         data = plan.barriers; break
      // Unmet Needs is a DERIVED section (pulled from resources tagged "unmet"
      // in Goals & Barriers) — those TBDs are already counted under Goals /
      // Barriers, and the section's own UI doesn't author TBDs. Counting the
      // legacy plan.unmetNeeds array here produced a phantom badge, so skip it.
      case "unmetNeeds":       return 0
      case "review":           data = plan.review; break
      default: return 0
    }
    return data ? this._countTBD(data) : 0
  }

  _sectionNameCount(plan, key) {
    if (!plan || key === "client") return 0
    const c = plan.client || {}
    const realFirst = (c.firstName || (c.name || "").split(/\s+/)[0] || "").trim()
    if (!realFirst || realFirst.length < 2) return 0
    const re = new RegExp("\\b" + realFirst.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "gi")
    const countIn = (obj) => {
      if (!obj) return 0
      if (typeof obj === "string") return (obj.match(re) || []).length
      if (Array.isArray(obj)) return obj.reduce((s, v) => s + countIn(v), 0)
      if (typeof obj === "object") return Object.values(obj).reduce((s, v) => s + countIn(v), 0)
      return 0
    }
    let data
    switch (key) {
      case "goals":            data = plan.goals; break
      case "barriers":         data = plan.barriers; break
      case "currentServices":  data = plan.currentServices; break
      case "unmetNeeds":       data = plan.unmetNeeds; break
      case "bigDreams":         data = plan.bigDreams; break
      case "about":            data = plan.about; break
      case "important":        data = plan.important; break
      case "working":          data = plan.working; break
      case "supportMatch":     data = plan.supportMatch; break
      default: return 0
    }
    return countIn(data)
  }

  _refreshSectionBadges() {
    if (!this.hasNavLinkTarget) return
    const plan = this._loadPlan()
    this.navLinkTargets.forEach(link => {
      const key = link.dataset.sectionKey
      if (!key) return
      const tbd  = this._sectionTBDCount(plan, key)
      const name = this._sectionNameCount(plan, key)
      const tbdBadge  = link.querySelector(".en-side-tbd-badge")
      const nameBadge = link.querySelector(".en-side-name-badge")
      if (tbdBadge) {
        tbdBadge.hidden = tbd === 0
        // Cap the visible count like a phone notification badge; the real
        // count stays in the title. Keeps the bubble from stretching past
        // the icon on the (rare) 100+ case.
        tbdBadge.textContent = tbd === 0 ? "" : tbd > 99 ? "99+" : String(tbd)
        tbdBadge.title = tbd > 0 ? `${tbd} TBD item${tbd > 1 ? "s" : ""}` : ""
      }
      if (nameBadge) {
        nameBadge.hidden = name === 0
        nameBadge.title = name > 0 ? `${name} real name usage — use [FirstName]` : ""
      }
    })
  }

  // Toolbar review counter — total open items (TBD markers + explicitly flagged
  // fields) via the shared scanner, so the number matches the Open Questions
  // section the button jumps to. Hidden when there's nothing to review.
  _refreshTbdFlag() {
    if (!this.hasTbdFlagTarget) return
    const plan = this._loadPlan()
    const n = plan ? findOpenQuestions(plan).length : 0
    this.tbdFlagTarget.hidden = n === 0
    if (this.hasTbdFlagCountTarget) this.tbdFlagCountTarget.textContent = n > 99 ? "99+" : String(n)
    this.tbdFlagTarget.title = n > 0
      ? `${n} item${n > 1 ? "s" : ""} to review (TBD / flagged) — click to open`
      : "No open items"
  }
}
